/**
 * CVE Details Page
 * Shows comprehensive vulnerability information with AI analysis
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useRefreshCVEFromNVDMutation, useReanalyzeCVEWithAIMutation } from '@/services/api';
import {
  ShieldCheckIcon as Shield,
  ExclamationTriangleIcon as AlertTriangle,
  CalendarIcon as Calendar,
  ArrowLeftIcon as ArrowLeft,
  CheckCircleIcon as CheckCircle,
  ArrowTrendingUpIcon as TrendingUp,
  ExclamationCircleIcon as Target,
  InformationCircleIcon as Info,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface CVEDetails {
  cve_id: string;
  description: string;
  severity: string;
  cvss_score: number;
  published_date: string;
  last_modified: string;
  vuln_status?: string;

  // CVSS Scores (all versions)
  cvss_scores?: {
    v31: { score: number; vector: string } | null;
    v30: { score: number; vector: string } | null;
    v2: { score: number; vector: string } | null;
  };

  // CVSS v3 Metrics
  cvss_metrics?: {
    attack_vector: string;
    attack_complexity: string;
    privileges_required: string;
    user_interaction: string;
    scope: string;
    confidentiality_impact: string;
    integrity_impact: string;
    availability_impact: string;
    exploitability_score: number;
    impact_score: number;
  };

  // Weakness and Platform Enumeration
  cwe_ids?: string[];
  cpe_configurations?: any[];

  // References
  reference_urls?: string[];
  reference_tags?: Record<string, string[]>;

  // GitHub Advisory
  github_advisory?: {
    ghsa_id: string;
    severity: string;
    published_date: string;
    updated_date: string;
  };

  ai_analysis?: {
    risk_score: number;
    urgency_level: string;
    severity_justification: string;
    real_world_impact: string;
    exploit_likelihood: string;
    detailed_analysis: string;
    deployment_recommendation: string;
    last_updated?: string;
  };
  technical_details?: {
    exploitability_assessment: string;
    attack_vector: string;
    attack_complexity: string;
    privileges_required: string;
  };
  affected_versions: any[];
  fixed_versions: any[];
  workarounds: string[];
  mitigation_recommendations: string[];
  affected_software: Array<{
    software_id: number;
    software_name: string;
    vendor: string;
    affected_versions: Array<{
      version_id: number;
      version_number: string;
      release_date: string;
      os: string;
      architecture: string;
    }>;
    patched_versions: Array<{
      version_id: number;
      version_number: string;
      release_date: string;
      os: string;
      architecture: string;
    }>;
    total_versions: number;
  }>;
}

export const CVEDetails: React.FC = () => {
  const { cve_id } = useParams<{ cve_id: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [cve, setCve] = useState<CVEDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshCVE, { isLoading: isRefreshing, isSuccess: refreshSuccess, isError: refreshError }] = useRefreshCVEFromNVDMutation();
  const [reanalyzeCVE, { isLoading: isReanalyzing, isSuccess: reanalyzeSuccess, isError: reanalyzeError }] = useReanalyzeCVEWithAIMutation();

  // TODO: Replace with actual admin check when Clerk role sync is working
  const isAdmin = isSignedIn; // Temporary: all logged-in users can access admin

  useEffect(() => {
    if (cve_id) {
      loadCVE();
    }
  }, [cve_id]);

  const loadCVE = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${API_BASE_URL}/ai/cve-analysis/${cve_id}`);
      if (!response.ok) throw new Error('Failed to load CVE');
      const json = await response.json();
      setCve(json.data || json); // Handle both { data: {...} } and direct CVE object
    } catch (error) {
      console.error('Failed to load CVE details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCVE = async () => {
    if (!cve_id) return;
    try {
      await refreshCVE(cve_id).unwrap();
      // Reload the CVE data after refresh
      setTimeout(() => loadCVE(), 2000); // Wait for enrichment to process
    } catch (error) {
      console.error('Failed to refresh CVE:', error);
    }
  };

  const handleReanalyzeCVE = async () => {
    if (!cve_id) return;
    try {
      await reanalyzeCVE(cve_id).unwrap();
      // Reload the CVE data after a delay to allow AI processing
      setTimeout(() => loadCVE(), 5000); // Wait longer for AI analysis
    } catch (error) {
      console.error('Failed to reanalyze CVE:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyber-blue-400"></div>
          <p className="text-gray-400 mt-4">Loading vulnerability details...</p>
        </div>
      </div>
    );
  }

  if (!cve) {
    return (
      <div className="p-8">
        <div className="bg-dark-bg-secondary rounded-lg p-12 text-center">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Vulnerability not found</p>
          <button
            onClick={() => navigate('/vulnerabilities')}
            className="mt-4 text-cyber-blue-400 hover:text-cyber-blue-300"
          >
            Return to vulnerabilities
          </button>
        </div>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'text-red-400 bg-red-900/30 border-red-700/50';
      case 'high':
        return 'text-orange-400 bg-orange-900/30 border-orange-700/50';
      case 'medium':
        return 'text-yellow-400 bg-yellow-900/30 border-yellow-700/50';
      case 'low':
        return 'text-green-400 bg-green-900/30 border-green-700/50';
      default:
        return 'text-gray-400 bg-gray-900/30 border-gray-700/50';
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/vulnerabilities')}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Vulnerabilities
      </button>

      {/* Header */}
      <div className="bg-dark-bg-secondary rounded-lg p-6 mb-6 border border-cyber-blue-500/30">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{cve.cve_id}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className={`px-3 py-1 rounded border text-sm font-semibold ${getSeverityColor(
                  cve.severity
                )}`}
              >
                {cve.severity ? cve.severity.toUpperCase() : 'UNKNOWN'}
              </span>
              <span className="text-gray-400">
                CVSS Score: <span className="text-white font-semibold">{cve.cvss_score?.toFixed(1)}</span>
              </span>
              {cve.ai_analysis?.risk_score && (
                <span className="text-gray-400">
                  AI Risk: <span className="text-orange-400 font-semibold">{cve.ai_analysis.risk_score.toFixed(1)}/10</span>
                </span>
              )}
              {cve.vuln_status && (
                <span className="px-3 py-1 rounded border border-gray-700 text-gray-300 text-sm">
                  {cve.vuln_status}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {cve.ai_analysis?.urgency_level && (
              <div className={`px-4 py-2 rounded border ${getSeverityColor(cve.ai_analysis.urgency_level)}`}>
                <AlertTriangle className="w-5 h-5 inline mr-2" />
                {cve.ai_analysis.urgency_level.toUpperCase()} URGENCY
              </div>
            )}

            {isAdmin && (
              <>
                <button
                  onClick={handleRefreshCVE}
                  disabled={isRefreshing}
                  className={`px-4 py-2 rounded border transition-all ${
                    isRefreshing
                      ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                      : refreshSuccess
                      ? 'bg-green-900/30 border-green-700/50 text-green-400'
                      : refreshError
                      ? 'bg-red-900/30 border-red-700/50 text-red-400'
                      : 'bg-cyber-blue-900/30 border-cyber-blue-700/50 text-cyber-blue-400 hover:bg-cyber-blue-900/50'
                  }`}
                  title="Refresh CVE data from NVD and re-enrich"
                >
                  <ArrowPathIcon className={`w-5 h-5 inline mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing...' : refreshSuccess ? 'Refreshed!' : 'Refresh from NVD'}
                </button>

                <button
                  onClick={handleReanalyzeCVE}
                  disabled={isReanalyzing}
                  className={`px-4 py-2 rounded border transition-all ${
                    isReanalyzing
                      ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                      : reanalyzeSuccess
                      ? 'bg-green-900/30 border-green-700/50 text-green-400'
                      : reanalyzeError
                      ? 'bg-red-900/30 border-red-700/50 text-red-400'
                      : 'bg-purple-900/30 border-purple-700/50 text-purple-400 hover:bg-purple-900/50'
                  }`}
                  title="Reanalyze CVE with AI for updated risk assessment"
                >
                  <svg className={`w-5 h-5 inline mr-2 ${isReanalyzing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {isReanalyzing ? 'Analyzing...' : reanalyzeSuccess ? 'Queued!' : 'Reanalyze with AI'}
                </button>
              </>
            )}
          </div>
        </div>

        <p className="text-gray-300 leading-relaxed">{cve.description}</p>

        <div className="flex items-center gap-6 mt-4 text-sm text-gray-400">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Published: {new Date(cve.published_date).toLocaleDateString()}
          </span>
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Modified: {new Date(cve.last_modified).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - AI Analysis */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Analysis */}
          {cve.ai_analysis && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-cyber-blue-400" />
                  AI Security Analysis
                </h2>
                {cve.ai_analysis.last_updated && (
                  <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Last analyzed: {new Date(cve.ai_analysis.last_updated).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Deployment Recommendation */}
              {cve.ai_analysis.deployment_recommendation && (
                <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4 mb-4">
                  <h3 className="text-orange-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Recommendation
                  </h3>
                  <p className="text-white">{cve.ai_analysis.deployment_recommendation}</p>
                </div>
              )}

              {/* Severity Justification */}
              {cve.ai_analysis.severity_justification && (
                <div className="mb-4">
                  <h3 className="text-white font-semibold mb-2">Why This Severity?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    {cve.ai_analysis.severity_justification}
                  </p>
                </div>
              )}

              {/* Real World Impact */}
              {cve.ai_analysis.real_world_impact && (
                <div className="mb-4">
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-red-400" />
                    Real-World Impact
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {cve.ai_analysis.real_world_impact}
                  </p>
                </div>
              )}

              {/* Exploit Likelihood */}
              {cve.ai_analysis.exploit_likelihood && (
                <div className="mb-4">
                  <h3 className="text-white font-semibold mb-2">Exploit Likelihood</h3>
                  <p className="text-gray-300">
                    <span className={`font-semibold ${
                      cve.ai_analysis.exploit_likelihood === 'active' ? 'text-red-400' :
                      cve.ai_analysis.exploit_likelihood === 'high' ? 'text-orange-400' :
                      cve.ai_analysis.exploit_likelihood === 'medium' ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {cve.ai_analysis.exploit_likelihood.toUpperCase()}
                    </span>
                  </p>
                </div>
              )}

              {/* Detailed Analysis */}
              {cve.ai_analysis.detailed_analysis && (
                <div>
                  <h3 className="text-white font-semibold mb-2">Detailed Analysis</h3>
                  <p className="text-gray-300 leading-relaxed">
                    {cve.ai_analysis.detailed_analysis}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Mitigation Recommendations */}
          {cve.mitigation_recommendations && cve.mitigation_recommendations.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-400" />
                Mitigation Recommendations
              </h2>
              <ul className="space-y-3">
                {cve.mitigation_recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Workarounds */}
          {cve.workarounds && cve.workarounds.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-2xl font-bold text-white mb-4">Workarounds</h2>
              <ul className="space-y-3">
                {cve.workarounds.map((workaround, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{workaround}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* References */}
          {cve.reference_urls && cve.reference_urls.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-2xl font-bold text-white mb-4">References</h2>
              <div className="space-y-2">
                {cve.reference_urls.map((url, idx) => (
                  <div key={idx} className="border-l-2 border-cyber-blue-500/50 pl-3 py-1">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyber-blue-400 hover:text-cyber-blue-300 text-sm break-all"
                    >
                      {url}
                    </a>
                    {cve.reference_tags && cve.reference_tags[url] && cve.reference_tags[url].length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {cve.reference_tags[url].map((tag, tagIdx) => (
                          <span
                            key={tagIdx}
                            className="px-2 py-0.5 bg-gray-800 border border-gray-700 text-gray-400 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Technical Details & Affected Software */}
        <div className="space-y-6">
          {/* CVSS Scores - All Versions */}
          {cve.cvss_scores && (cve.cvss_scores.v31 || cve.cvss_scores.v30 || cve.cvss_scores.v2) && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">CVSS Scores</h2>
              <div className="space-y-3">
                {cve.cvss_scores.v31 && (
                  <div className="border-l-4 border-cyber-blue-500 pl-4">
                    <p className="text-gray-400 text-sm">CVSS v3.1</p>
                    <p className="text-white text-2xl font-bold">{cve.cvss_scores.v31.score.toFixed(1)}</p>
                    <p className="text-gray-400 text-xs font-mono mt-1 break-all">{cve.cvss_scores.v31.vector}</p>
                  </div>
                )}
                {cve.cvss_scores.v30 && (
                  <div className="border-l-4 border-purple-500 pl-4">
                    <p className="text-gray-400 text-sm">CVSS v3.0</p>
                    <p className="text-white text-2xl font-bold">{cve.cvss_scores.v30.score.toFixed(1)}</p>
                    <p className="text-gray-400 text-xs font-mono mt-1 break-all">{cve.cvss_scores.v30.vector}</p>
                  </div>
                )}
                {cve.cvss_scores.v2 && (
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <p className="text-gray-400 text-sm">CVSS v2.0</p>
                    <p className="text-white text-2xl font-bold">{cve.cvss_scores.v2.score.toFixed(1)}</p>
                    <p className="text-gray-400 text-xs font-mono mt-1 break-all">{cve.cvss_scores.v2.vector}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CVSS v3 Metrics */}
          {cve.cvss_metrics && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">Attack Metrics</h2>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-400">Attack Vector</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-red-900/30 border border-red-700/50 text-red-400 rounded text-xs font-semibold">
                      {cve.cvss_metrics.attack_vector}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-400">Attack Complexity</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-orange-900/30 border border-orange-700/50 text-orange-400 rounded text-xs font-semibold">
                      {cve.cvss_metrics.attack_complexity}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-400">Privileges Required</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-yellow-900/30 border border-yellow-700/50 text-yellow-400 rounded text-xs font-semibold">
                      {cve.cvss_metrics.privileges_required}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-400">User Interaction</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-blue-900/30 border border-blue-700/50 text-blue-400 rounded text-xs font-semibold">
                      {cve.cvss_metrics.user_interaction}
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-700">
                  <p className="text-gray-400 mb-2">Impact</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Confidentiality</p>
                      <span className="inline-block mt-1 px-2 py-1 bg-purple-900/30 border border-purple-700/50 text-purple-400 rounded text-xs font-semibold">
                        {cve.cvss_metrics.confidentiality_impact}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Integrity</p>
                      <span className="inline-block mt-1 px-2 py-1 bg-purple-900/30 border border-purple-700/50 text-purple-400 rounded text-xs font-semibold">
                        {cve.cvss_metrics.integrity_impact}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Availability</p>
                      <span className="inline-block mt-1 px-2 py-1 bg-purple-900/30 border border-purple-700/50 text-purple-400 rounded text-xs font-semibold">
                        {cve.cvss_metrics.availability_impact}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-700 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-400">Exploitability Score</p>
                    <p className="text-white text-lg font-bold">{cve.cvss_metrics.exploitability_score?.toFixed(1)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Impact Score</p>
                    <p className="text-white text-lg font-bold">{cve.cvss_metrics.impact_score?.toFixed(1)}</p>
                  </div>
                </div>

                {cve.cvss_metrics.scope && (
                  <div className="pt-3 border-t border-gray-700">
                    <p className="text-gray-400">Scope</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-green-900/30 border border-green-700/50 text-green-400 rounded text-xs font-semibold">
                      {cve.cvss_metrics.scope}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CWE IDs */}
          {cve.cwe_ids && cve.cwe_ids.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">Weakness Types (CWE)</h2>
              <div className="flex flex-wrap gap-2">
                {cve.cwe_ids.map((cwe, idx) => (
                  <a
                    key={idx}
                    href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-cyber-blue-900/30 border border-cyber-blue-700/50 text-cyber-blue-400 rounded text-sm hover:bg-cyber-blue-900/50 transition-colors"
                  >
                    {cwe}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* GitHub Advisory */}
          {cve.github_advisory && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">GitHub Advisory</h2>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-400">Advisory ID</p>
                  <a
                    href={`https://github.com/advisories/${cve.github_advisory.ghsa_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyber-blue-400 hover:text-cyber-blue-300 font-mono"
                  >
                    {cve.github_advisory.ghsa_id}
                  </a>
                </div>
                {cve.github_advisory.severity && (
                  <div>
                    <p className="text-gray-400">GitHub Severity</p>
                    <p className="text-white font-semibold capitalize">{cve.github_advisory.severity}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Technical Details */}
          {cve.technical_details && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">Technical Details</h2>
              <div className="space-y-3 text-sm">
                {cve.technical_details.attack_vector && (
                  <div>
                    <p className="text-gray-400">Attack Vector</p>
                    <p className="text-white font-semibold capitalize">
                      {cve.technical_details.attack_vector}
                    </p>
                  </div>
                )}
                {cve.technical_details.attack_complexity && (
                  <div>
                    <p className="text-gray-400">Attack Complexity</p>
                    <p className="text-white font-semibold capitalize">
                      {cve.technical_details.attack_complexity}
                    </p>
                  </div>
                )}
                {cve.technical_details.privileges_required && (
                  <div>
                    <p className="text-gray-400">Privileges Required</p>
                    <p className="text-white font-semibold capitalize">
                      {cve.technical_details.privileges_required}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Affected Software */}
          {cve.affected_software && cve.affected_software.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4">Affected Software & Versions</h2>
              <div className="space-y-6">
                {cve.affected_software.map((software) => (
                  <div key={software.software_id} className="border border-gray-700 rounded-lg p-4">
                    {/* Software Header */}
                    <div className="mb-4">
                      <Link
                        to={`/software/${software.software_id}`}
                        className="text-lg font-bold text-white hover:text-cyber-blue-400 transition-colors"
                      >
                        {software.software_name}
                      </Link>
                      {software.vendor && (
                        <p className="text-gray-400 text-sm mt-1">Vendor: {software.vendor}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-red-400">
                          {software.affected_versions.length} Affected
                        </span>
                        <span className="text-green-400">
                          {software.patched_versions.length} Patched
                        </span>
                        <span className="text-gray-400">
                          {software.total_versions} Total
                        </span>
                      </div>
                    </div>

                    {/* Affected Versions */}
                    {software.affected_versions.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Vulnerable Versions
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          {software.affected_versions.slice(0, 5).map((version) => (
                            <div
                              key={version.version_id}
                              className="bg-red-900/20 border border-red-700/50 rounded px-3 py-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-white font-mono text-sm">
                                  {version.version_number}
                                </span>
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  {version.os && (
                                    <span className="px-2 py-0.5 bg-gray-800 rounded">
                                      {version.os}
                                    </span>
                                  )}
                                  {version.architecture && (
                                    <span className="px-2 py-0.5 bg-gray-800 rounded">
                                      {version.architecture}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {version.release_date && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Released: {new Date(version.release_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          ))}
                          {software.affected_versions.length > 5 && (
                            <p className="text-xs text-gray-400 text-center py-2">
                              + {software.affected_versions.length - 5} more affected versions
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Patched Versions */}
                    {software.patched_versions.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Patched Versions
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          {software.patched_versions.slice(0, 5).map((version) => (
                            <div
                              key={version.version_id}
                              className="bg-green-900/20 border border-green-700/50 rounded px-3 py-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-white font-mono text-sm">
                                  {version.version_number}
                                </span>
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  {version.os && (
                                    <span className="px-2 py-0.5 bg-gray-800 rounded">
                                      {version.os}
                                    </span>
                                  )}
                                  {version.architecture && (
                                    <span className="px-2 py-0.5 bg-gray-800 rounded">
                                      {version.architecture}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {version.release_date && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Released: {new Date(version.release_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          ))}
                          {software.patched_versions.length > 5 && (
                            <p className="text-xs text-gray-400 text-center py-2">
                              + {software.patched_versions.length - 5} more patched versions
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fixed Versions */}
          {cve.fixed_versions && cve.fixed_versions.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-6 border border-cyber-blue-500/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Patched Versions
              </h2>
              <div className="space-y-2">
                {cve.fixed_versions.map((version: string, idx: number) => (
                  <div
                    key={idx}
                    className="px-3 py-2 bg-green-900/20 border border-green-700/50 rounded text-green-400 font-mono text-sm"
                  >
                    {version}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
